/**
 * Year-end drafts (series B) and the year-open roll (IB + series A), per
 * entity type. Pure; the year-end service (workstream C) books them.
 *
 * Sign convention for balances: debit +, credit −. `resultOre` is the year's
 * result as a signed profit: positive = vinst, negative = förlust
 * (= −Σ balances of 3000–8989).
 */
import { CORPORATE_TAX_RATE } from '../bas/constants';
import { formatSek } from '../money';
import type { Ore } from '../money';
import type { EntityType, IsoDate, VerifikationRow } from '../types';
import type { VerifikationDraft } from '../verifikationer/rules';

export const RESULT_CLOSING_ACCOUNT = 8999;

/** Swedish percent copy: 0.206 → '20,6' (decimal comma, at most one decimal). */
export function formatPercent(rate: number): string {
  return String(Math.round(rate * 1000) / 10).replace('.', ',');
}
export const TAX_EXPENSE_ACCOUNT = 8910;
export const TAX_LIABILITY_ACCOUNT = 2512;

/** Where årets resultat lands in the balance sheet. */
export function resultAccountFor(entity: EntityType): number {
  switch (entity) {
    case 'aktiebolag':
    case 'bostadsrattsforening':
      return 2099;
    case 'enskild_firma':
      return 2019;
    case 'ideell_forening':
      return 2069;
    default: {
      const exhaustive: never = entity;
      throw new Error(`Unknown entity type ${String(exhaustive)}`);
    }
  }
}

/** Where last year's result is rolled on the first day of the next year. */
export function rollTargetAccountFor(entity: EntityType): number {
  switch (entity) {
    case 'aktiebolag':
    case 'bostadsrattsforening':
      return 2098;
    case 'enskild_firma':
      return 2010;
    case 'ideell_forening':
      return 2067;
    default: {
      const exhaustive: never = entity;
      throw new Error(`Unknown entity type ${String(exhaustive)}`);
    }
  }
}

/** Whether the entity books income tax on its result (AB, BRF). */
export function booksCorporateTax(entity: EntityType): boolean {
  switch (entity) {
    case 'aktiebolag':
    case 'bostadsrattsforening':
      return true;
    case 'enskild_firma':
    case 'ideell_forening':
      return false;
    default: {
      const exhaustive: never = entity;
      throw new Error(`Unknown entity type ${String(exhaustive)}`);
    }
  }
}

export interface YearEndDraft extends VerifikationDraft {
  series: 'B';
  step: 'tax' | 'result';
}

/**
 * Series-B drafts closing a year: an optional tax draft (AB/BRF only,
 * D 8910 / K 2512) followed by the result draft (profit: D 8999 / K result
 * account; loss: the reverse). `resultOre` is the result before tax.
 * A zero net result yields no result draft.
 */
export function yearEndDrafts(entity: EntityType, resultOre: Ore, taxOre: Ore | null, fyEnd: IsoDate): YearEndDraft[] {
  if (!Number.isInteger(resultOre)) throw new RangeError('resultOre must be an integer öre amount');
  const drafts: YearEndDraft[] = [];
  const tax = booksCorporateTax(entity) && taxOre !== null && Number.isInteger(taxOre) && taxOre > 0 ? taxOre : 0;
  if (tax > 0) {
    drafts.push({
      series: 'B',
      step: 'tax',
      date: fyEnd,
      description: `Beräknad skatt på årets resultat (${formatPercent(CORPORATE_TAX_RATE)} %)`,
      rows: [
        { accountNumber: TAX_EXPENSE_ACCOUNT, debit: tax, credit: 0 },
        { accountNumber: TAX_LIABILITY_ACCOUNT, debit: 0, credit: tax },
      ],
    });
  }
  const net = resultOre - tax;
  const resultAccount = resultAccountFor(entity);
  if (net > 0) {
    drafts.push({
      series: 'B',
      step: 'result',
      date: fyEnd,
      description: `Årets resultat: vinst ${formatSek(net)} kr`,
      rows: [
        { accountNumber: RESULT_CLOSING_ACCOUNT, debit: net, credit: 0 },
        { accountNumber: resultAccount, debit: 0, credit: net },
      ],
    });
  } else if (net < 0) {
    drafts.push({
      series: 'B',
      step: 'result',
      date: fyEnd,
      description: `Årets resultat: förlust ${formatSek(-net)} kr`,
      rows: [
        { accountNumber: resultAccount, debit: -net, credit: 0 },
        { accountNumber: RESULT_CLOSING_ACCOUNT, debit: 0, credit: -net },
      ],
    });
  }
  return drafts;
}

export interface YearOpenDraft extends VerifikationDraft {
  series: 'IB' | 'A';
  step: 'opening_balances' | 'roll';
}

/** Rows that carry a balance forward: debit balances debit, credit balances credit. */
function balanceRows(closing: ReadonlyMap<number, Ore>, predicate: (n: number) => boolean): VerifikationRow[] {
  const rows: VerifikationRow[] = [];
  for (const accountNumber of [...closing.keys()].sort((a, b) => a - b)) {
    if (!predicate(accountNumber)) continue;
    const balance = closing.get(accountNumber) ?? 0;
    if (balance === 0) continue;
    rows.push(balance > 0 ? { accountNumber, debit: balance, credit: 0 } : { accountNumber, debit: 0, credit: -balance });
  }
  return rows;
}

/** Rows that zero the given accounts, returning the net moved (debit +). */
function zeroingRows(closing: ReadonlyMap<number, Ore>, numbers: readonly number[]): { rows: VerifikationRow[]; net: Ore } {
  const rows: VerifikationRow[] = [];
  let net = 0;
  for (const accountNumber of numbers) {
    const balance = closing.get(accountNumber) ?? 0;
    if (balance === 0) continue;
    rows.push(balance > 0 ? { accountNumber, debit: 0, credit: balance } : { accountNumber, debit: -balance, credit: 0 });
    net -= balance;
  }
  return { rows, net };
}

/**
 * Drafts for the first day of the next year:
 *   1. IB: every balance account (1000–2999) with a non-zero closing balance
 *   2. A-series roll of last year's result:
 *        AB/BRF     2099 → 2098 (2098 → 2091/2898 after stämman is manual)
 *        EF         2019 → 2010, and 2011/2013/2017/2018 netted into 2010
 *        förening   2069 → 2067
 * `closing` holds closing balances after the series-B result booking, so the
 * result account carries the year's result.
 */
export function yearOpenRoll(entity: EntityType, closing: ReadonlyMap<number, Ore>, nextFyStart: IsoDate): YearOpenDraft[] {
  const drafts: YearOpenDraft[] = [];
  const ibRows = balanceRows(closing, (n) => n >= 1000 && n <= 2999);
  if (ibRows.length > 0) {
    drafts.push({ series: 'IB', step: 'opening_balances', date: nextFyStart, description: 'Ingående balanser', rows: ibRows });
  }
  const accountsToRoll = rollSourceAccountsFor(entity);
  const { rows, net } = zeroingRows(closing, accountsToRoll);
  if (rows.length > 0) {
    const target = rollTargetAccountFor(entity);
    // `net` is what left the source accounts (debit +); the target takes the opposite side.
    if (net > 0) rows.push({ accountNumber: target, debit: 0, credit: net });
    else if (net < 0) rows.push({ accountNumber: target, debit: -net, credit: 0 });
    drafts.push({ series: 'A', step: 'roll', date: nextFyStart, description: 'Omföring av föregående års resultat', rows });
  }
  return drafts;
}

/** Accounts emptied into the roll target on day one. */
export function rollSourceAccountsFor(entity: EntityType): readonly number[] {
  switch (entity) {
    case 'aktiebolag':
    case 'bostadsrattsforening':
      return [2099];
    case 'enskild_firma':
      return [2011, 2013, 2017, 2018, 2019];
    case 'ideell_forening':
      return [2069];
    default: {
      const exhaustive: never = entity;
      throw new Error(`Unknown entity type ${String(exhaustive)}`);
    }
  }
}
